import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, CalendarIcon, XIcon, DownloadIcon, FileSpreadsheetIcon, UsersIcon, LockIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Patient, Gender, MedicalHistory } from '../../types';
import { formatDate, formatTime, getGenderName, getStatusName } from '../../utils/helpers';
import { validatePatientForm } from '../../utils/validation';
import { exportPatientsToExcel } from '../../utils/excelExport';
export function Patients() {
  const {
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    updateMedicalHistory,
    insurances,
    getInsurance,
    getAppointmentsByPatient,
    getDoctor,
    getService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('patients');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    age: 0,
    gender: 'masculino' as Gender,
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    insuranceId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Medical history form state
  const [medicalData, setMedicalData] = useState<MedicalHistory | null>(null);
  const filteredPatients = patients.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.email.toLowerCase().includes(searchTerm.toLowerCase()) || p.phone.includes(searchTerm));
  const genderOptions = [{
    value: 'masculino',
    label: 'Masculino'
  }, {
    value: 'femenino',
    label: 'Femenino'
  }, {
    value: 'otro',
    label: 'Otro'
  }];
  const insuranceOptions = [{
    value: '',
    label: 'Sin seguro'
  }, ...insurances.map(i => ({
    value: i.id,
    label: i.name
  }))];
  const openNewForm = () => {
    if (!canCreate('patients')) return;
    setSelectedPatient(null);
    setFormData({
      name: '',
      age: 0,
      gender: 'masculino',
      phone: '',
      email: '',
      address: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      insuranceId: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (patient: Patient) => {
    if (!canEdit('patients')) return;
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      phone: patient.phone,
      email: patient.email,
      address: patient.address,
      emergencyContactName: patient.emergencyContact?.name || '',
      emergencyContactRelationship: patient.emergencyContact?.relationship || '',
      emergencyContactPhone: patient.emergencyContact?.phone || '',
      insuranceId: patient.insuranceId || ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openView = (patient: Patient) => {
    setSelectedPatient(patient);
    setMedicalData(patient.medicalHistory);
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePatientForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const patientData = {
      name: formData.name,
      age: formData.age,
      gender: formData.gender,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      emergencyContact: {
        name: formData.emergencyContactName,
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      },
      insuranceId: formData.insuranceId || null
    };
    if (selectedPatient) {
      updatePatient(selectedPatient.id, patientData);
    } else {
      addPatient(patientData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('patients')) return;
    if (selectedPatient) {
      deletePatient(selectedPatient.id);
      setIsDeleteConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const saveMedicalHistory = () => {
    if (readOnly) return;
    if (selectedPatient && medicalData) {
      updateMedicalHistory(selectedPatient.id, medicalData);
    }
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1124" />
  }, {
    id: 'history',
    label: 'Historial Médico',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1125" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-1126" />
  }];
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportPatientsToExcel(patients, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1127">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1128">
        <div data-id="element-1129">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1130">
            Pacientes
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1131">
            Gestión de pacientes y expedientes médicos
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-1132">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('patients') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1134" />} onClick={openNewForm} data-id="element-1133">
            Nuevo Paciente
          </Button>}
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30 border-sky-200 dark:border-sky-800" data-id="element-1135">
        <div className="flex items-center justify-between" data-id="element-1136">
          <div className="flex items-center gap-4" data-id="element-1137">
            <div className="w-14 h-14 rounded-xl bg-sky-500 dark:bg-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/20" data-id="element-1138">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1139" />
            </div>
            <div data-id="element-1140">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1141">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1142">
                Descarga todos los pacientes en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-1143">
                <div className="flex items-center gap-1.5 text-sm text-sky-700 dark:text-sky-400" data-id="element-1144">
                  <UsersIcon className="w-4 h-4" data-id="element-1145" />
                  <span className="font-medium" data-id="element-1146">
                    {patients.length} pacientes
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1147">
                  • Incluye: Nombre, Edad, Contacto, Seguro, Historial
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1149" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-sky-500/20 hover:shadow-xl hover:shadow-sky-500/30 transition-all duration-200" data-id="element-1148">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Search */}
      <Card data-id="element-1150">
        <div className="flex items-center gap-4" data-id="element-1151">
          <div className="flex-1 relative" data-id="element-1152">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1153" />
            <Input placeholder="Buscar por nombre, correo o teléfono..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1154" />
          </div>
        </div>
      </Card>

      {/* Patients List */}
      <Card padding="none" data-id="element-1155">
        <div className="overflow-x-auto" data-id="element-1156">
          <table className="w-full" data-id="element-1157">
            <thead data-id="element-1158">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1159">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1160">
                  Paciente
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1161">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1162">
                  Seguro
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1163">
                  Edad
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1164">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1165">
              {filteredPatients.length === 0 ? <tr data-id="element-1166">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1167">
                    No se encontraron pacientes
                  </td>
                </tr> : filteredPatients.map(patient => {
              const insurance = patient.insuranceId ? getInsurance(patient.insuranceId) : null;
              return <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1168">
                      <td className="py-4 px-6" data-id="element-1169">
                        <div data-id="element-1170">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-1171">
                            {patient.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1172">
                            {getGenderName(patient.gender)}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1173">
                        <div className="space-y-1" data-id="element-1174">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1" data-id="element-1175">
                            <PhoneIcon className="w-3 h-3" data-id="element-1176" /> {patient.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-1177">
                            <MailIcon className="w-3 h-3" data-id="element-1178" /> {patient.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1179">
                        {insurance ? <Badge variant="info" data-id="element-1180">{insurance.name}</Badge> : <span className="text-sm text-gray-400" data-id="element-1181">
                            Sin seguro
                          </span>}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-1182">
                        {patient.age} años
                      </td>
                      <td className="py-4 px-6" data-id="element-1183">
                        <div className="flex items-center justify-end gap-2" data-id="element-1184">
                          <button onClick={() => openView(patient)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-1185">
                            <EyeIcon className="w-4 h-4" data-id="element-1186" />
                          </button>
                          {canEdit('patients') ? <button onClick={() => openEditForm(patient)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-1187">
                              <EditIcon className="w-4 h-4" data-id="element-1188" />
                            </button> : <button disabled className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Sin permisos para editar" data-id="element-1189">
                              <LockIcon className="w-4 h-4" data-id="element-1190" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Patient Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPatient ? 'Editar Paciente' : 'Nuevo Paciente'} size="lg" data-id="element-1191">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1192">
          <div className="grid grid-cols-2 gap-4" data-id="element-1193">
            <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} error={formErrors.name} data-id="element-1194" />
            <div className="grid grid-cols-2 gap-4" data-id="element-1195">
              <Input type="number" label="Edad" value={formData.age} onChange={e => setFormData({
              ...formData,
              age: parseInt(e.target.value) || 0
            })} error={formErrors.age} data-id="element-1196" />
              <Select label="Sexo" options={genderOptions} value={formData.gender} onChange={value => setFormData({
              ...formData,
              gender: value as Gender
            })} error={formErrors.gender} data-id="element-1197" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1198">
            <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} error={formErrors.phone} data-id="element-1199" />
            <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} error={formErrors.email} data-id="element-1200" />
          </div>

          <Input label="Dirección" value={formData.address} onChange={e => setFormData({
          ...formData,
          address: e.target.value
        })} data-id="element-1201" />

          {/* Emergency Contact Section */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1202">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1203">
              Contacto de Emergencia
            </h3>
            <div className="space-y-4" data-id="element-1204">
              <div className="grid grid-cols-2 gap-4" data-id="element-1205">
                <Input label="Nombre Completo" value={formData.emergencyContactName} onChange={e => setFormData({
                ...formData,
                emergencyContactName: e.target.value
              })} placeholder="Nombre del contacto" data-id="element-1206" />
                <Input label="Parentesco" value={formData.emergencyContactRelationship} onChange={e => setFormData({
                ...formData,
                emergencyContactRelationship: e.target.value
              })} placeholder="Ej: Madre, Esposo, Hermano" data-id="element-1207" />
              </div>
              <Input label="Teléfono de Emergencia" value={formData.emergencyContactPhone} onChange={e => setFormData({
              ...formData,
              emergencyContactPhone: e.target.value
            })} placeholder="Número de contacto" data-id="element-1208" />
            </div>
          </div>

          <Select label="Seguro Médico" options={insuranceOptions} value={formData.insuranceId} onChange={value => setFormData({
          ...formData,
          insuranceId: value
        })} data-id="element-1209" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-1210">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1211">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1212">
              {selectedPatient ? 'Guardar Cambios' : 'Crear Paciente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Patient View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title={selectedPatient?.name || 'Paciente'} size="xl" data-id="element-1213">
        {selectedPatient && <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1214">
            <TabPanel id="info" activeTab={activeTab} data-id="element-1215">
              <div className="space-y-6" data-id="element-1216">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-6" data-id="element-1217">
                  <div data-id="element-1218">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1219">
                      Edad
                    </h4>
                    <p className="text-gray-900 dark:text-white" data-id="element-1220">
                      {selectedPatient.age} años
                    </p>
                  </div>
                  <div data-id="element-1221">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1222">
                      Sexo
                    </h4>
                    <p className="text-gray-900 dark:text-white" data-id="element-1223">
                      {getGenderName(selectedPatient.gender)}
                    </p>
                  </div>
                  <div data-id="element-1224">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1225">
                      Teléfono
                    </h4>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1226">
                      <PhoneIcon className="w-4 h-4" data-id="element-1227" /> {selectedPatient.phone}
                    </p>
                  </div>
                  <div data-id="element-1228">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1229">
                      Correo
                    </h4>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1230">
                      <MailIcon className="w-4 h-4" data-id="element-1231" /> {selectedPatient.email}
                    </p>
                  </div>
                  <div className="col-span-2" data-id="element-1232">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1233">
                      Dirección
                    </h4>
                    <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1234">
                      <MapPinIcon className="w-4 h-4" data-id="element-1235" />{' '}
                      {selectedPatient.address}
                    </p>
                  </div>
                  <div data-id="element-1236">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1237">
                      Seguro Médico
                    </h4>
                    <p className="text-gray-900 dark:text-white" data-id="element-1238">
                      {selectedPatient.insuranceId ? getInsurance(selectedPatient.insuranceId)?.name : 'Sin seguro'}
                    </p>
                  </div>
                </div>

                {/* Emergency Contact Section */}
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-1239">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1240">
                    Contacto de Emergencia
                  </h3>
                  <div className="grid grid-cols-2 gap-6" data-id="element-1241">
                    <div data-id="element-1242">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1243">
                        Nombre
                      </h4>
                      <p className="text-gray-900 dark:text-white" data-id="element-1244">
                        {selectedPatient.emergencyContact?.name || '-'}
                      </p>
                    </div>
                    <div data-id="element-1245">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1246">
                        Parentesco
                      </h4>
                      <p className="text-gray-900 dark:text-white" data-id="element-1247">
                        {selectedPatient.emergencyContact?.relationship || '-'}
                      </p>
                    </div>
                    <div data-id="element-1248">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1249">
                        Teléfono
                      </h4>
                      <p className="text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1250">
                        <PhoneIcon className="w-4 h-4" data-id="element-1251" />
                        {selectedPatient.emergencyContact?.phone || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {!readOnly && <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1252">
                    {canEdit('patients') && <Button variant="outline" onClick={() => openEditForm(selectedPatient)} data-id="element-1253">
                        <EditIcon className="w-4 h-4 mr-2" data-id="element-1254" /> Editar
                      </Button>}
                    {canDelete('patients') && <Button variant="danger" onClick={() => setIsDeleteConfirmOpen(true)} data-id="element-1255">
                        <TrashIcon className="w-4 h-4 mr-2" data-id="element-1256" /> Eliminar
                      </Button>}
                  </div>}
              </div>
            </TabPanel>

            <TabPanel id="history" activeTab={activeTab} data-id="element-1257">
              {medicalData && <div className="space-y-6" data-id="element-1258">
                  {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1259">
                      <LockIcon className="w-4 h-4" data-id="element-1260" />
                      <span data-id="element-1261">
                        Solo tienes permisos de lectura. No puedes editar el
                        historial médico.
                      </span>
                    </div>}

                  {/* Initial Interview */}
                  <div data-id="element-1262">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1263">
                      Entrevista Inicial
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1264">
                      <Textarea label="Antecedentes" value={medicalData.initialInterview.background} onChange={e => setMedicalData({
                  ...medicalData,
                  initialInterview: {
                    ...medicalData.initialInterview,
                    background: e.target.value
                  }
                })} rows={2} disabled={readOnly} data-id="element-1265" />
                      <Textarea label="Motivo de Visita" value={medicalData.initialInterview.visitReason} onChange={e => setMedicalData({
                  ...medicalData,
                  initialInterview: {
                    ...medicalData.initialInterview,
                    visitReason: e.target.value
                  }
                })} rows={2} disabled={readOnly} data-id="element-1266" />
                      <Input label="Alergias" value={medicalData.initialInterview.allergies} onChange={e => setMedicalData({
                  ...medicalData,
                  initialInterview: {
                    ...medicalData.initialInterview,
                    allergies: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1267" />
                      <Input label="Medicamentos Actuales" value={medicalData.initialInterview.currentMedications} onChange={e => setMedicalData({
                  ...medicalData,
                  initialInterview: {
                    ...medicalData.initialInterview,
                    currentMedications: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1268" />
                      <Textarea label="Historial Familiar" value={medicalData.initialInterview.familyHistory} onChange={e => setMedicalData({
                  ...medicalData,
                  initialInterview: {
                    ...medicalData.initialInterview,
                    familyHistory: e.target.value
                  }
                })} rows={2} className="col-span-2" disabled={readOnly} data-id="element-1269" />
                    </div>
                  </div>

                  {/* Symptoms */}
                  <div data-id="element-1270">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1271">
                      Revisión de Síntomas
                    </h3>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1272">
                      <Textarea label="Descripción" value={medicalData.symptoms.description} onChange={e => setMedicalData({
                  ...medicalData,
                  symptoms: {
                    ...medicalData.symptoms,
                    description: e.target.value
                  }
                })} rows={2} disabled={readOnly} data-id="element-1273" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-1274">
                        <Input label="Duración" value={medicalData.symptoms.duration} onChange={e => setMedicalData({
                    ...medicalData,
                    symptoms: {
                      ...medicalData.symptoms,
                      duration: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1275" />
                        <Input label="Severidad" value={medicalData.symptoms.severity} onChange={e => setMedicalData({
                    ...medicalData,
                    symptoms: {
                      ...medicalData.symptoms,
                      severity: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1276" />
                      </div>
                    </div>
                  </div>

                  {/* Physical Exam */}
                  <div data-id="element-1277">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1278">
                      Exploración Física
                    </h3>
                    <div className="grid grid-cols-3 gap-4" data-id="element-1279">
                      <Input label="Presión Arterial" value={medicalData.physicalExam.bloodPressure} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    bloodPressure: e.target.value
                  }
                })} placeholder="120/80" disabled={readOnly} data-id="element-1280" />
                      <Input label="Azúcar en Sangre" value={medicalData.physicalExam.bloodSugar} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    bloodSugar: e.target.value
                  }
                })} placeholder="mg/dL" disabled={readOnly} data-id="element-1281" />
                      <Input label="Ritmo Cardíaco" value={medicalData.physicalExam.heartRate} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    heartRate: e.target.value
                  }
                })} placeholder="bpm" disabled={readOnly} data-id="element-1282" />
                      <Input label="Pulmones" value={medicalData.physicalExam.lungs} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    lungs: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1283" />
                      <Input label="Ojos" value={medicalData.physicalExam.eyes} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    eyes: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1284" />
                      <Input label="Garganta" value={medicalData.physicalExam.throat} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    throat: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1285" />
                      <Input label="Abdomen" value={medicalData.physicalExam.abdomen} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    abdomen: e.target.value
                  }
                })} disabled={readOnly} data-id="element-1286" />
                      <Input label="Peso" value={medicalData.physicalExam.weight} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    weight: e.target.value
                  }
                })} placeholder="kg" disabled={readOnly} data-id="element-1287" />
                      <Input label="Altura" value={medicalData.physicalExam.height} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    height: e.target.value
                  }
                })} placeholder="cm" disabled={readOnly} data-id="element-1288" />
                      <Input label="Temperatura" value={medicalData.physicalExam.temperature} onChange={e => setMedicalData({
                  ...medicalData,
                  physicalExam: {
                    ...medicalData.physicalExam,
                    temperature: e.target.value
                  }
                })} placeholder="°C" disabled={readOnly} data-id="element-1289" />
                    </div>
                  </div>

                  {/* Diagnosis */}
                  <div data-id="element-1290">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1291">
                      Diagnóstico y Recomendaciones
                    </h3>
                    <div className="space-y-4" data-id="element-1292">
                      <Textarea label="Diagnóstico" value={medicalData.diagnosis} onChange={e => setMedicalData({
                  ...medicalData,
                  diagnosis: e.target.value
                })} rows={3} disabled={readOnly} data-id="element-1293" />
                      <Textarea label="Recomendaciones" value={medicalData.recommendations} onChange={e => setMedicalData({
                  ...medicalData,
                  recommendations: e.target.value
                })} rows={3} disabled={readOnly} data-id="element-1294" />
                    </div>
                  </div>

                  {!readOnly && <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1295">
                      <Button onClick={saveMedicalHistory} data-id="element-1296">
                        Guardar Historial Médico
                      </Button>
                    </div>}
                </div>}
            </TabPanel>

            <TabPanel id="appointments" activeTab={activeTab} data-id="element-1297">
              <div className="space-y-4" data-id="element-1298">
                {getAppointmentsByPatient(selectedPatient.id).length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-1299">
                    No hay citas registradas
                  </p> : getAppointmentsByPatient(selectedPatient.id).map(apt => {
              const doctor = getDoctor(apt.doctorId);
              const service = getService(apt.serviceId);
              return <div key={apt.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-1300">
                        <div data-id="element-1301">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-1302">
                            {apt.date} - {formatTime(apt.time)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1303">
                            Dr. {doctor?.name} • {service?.name}
                          </p>
                        </div>
                        <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-1304">
                          {getStatusName(apt.status)}
                        </Badge>
                      </div>;
            })}
              </div>
            </TabPanel>
          </Tabs>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1305">
        <div className="space-y-4" data-id="element-1306">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1307">
            ¿Está seguro de que desea eliminar a este paciente? Esta acción no
            se puede deshacer.
          </p>
          <div className="flex justify-end gap-3" data-id="element-1308">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1309">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1310">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}